import { compareIso, todayIso } from '@apka/core'
import { useCallback, useState } from 'react'
import type { IsoDate } from '@apka/core'
import type { Kategoria, Platnosc, Pozycja } from '../dane/typy.js'
import { IkonaPtaszek } from './Ikony.js'
import { skrotMiesiaca, waluta, zl } from './format.js'
import './lista-platnosci.css'

export interface Props {
  platnosci: readonly Platnosc[]
  pozycje: ReadonlyMap<string, Pozycja>
  kategorie: ReadonlyMap<string, Kategoria>
  dzis?: IsoDate
  onPrzelacz(platnosc: Platnosc): void
  pustaTresc?: string
}

function stanTerminu(platnosc: Platnosc, dzis: IsoDate): 'zalegle' | 'dzis' | 'przyszle' {
  if (platnosc.status !== 'planned') return 'przyszle'
  const roznica = compareIso(platnosc.dueDate, dzis)
  if (roznica < 0) return 'zalegle'
  return roznica === 0 ? 'dzis' : 'przyszle'
}

/** Kwota w walucie pozycji; złotówkę pokazujemy pod spodem tylko wtedy, gdy się różni. */
function kwoty(platnosc: Platnosc): { glowna: string; pomocnicza: string | null } {
  const kwota = platnosc.actualAmountMinor ?? platnosc.plannedAmountMinor
  const kod = platnosc.actualCurrency ?? platnosc.plannedCurrency
  if (kod === 'PLN') return { glowna: zl(kwota), pomocnicza: null }
  return {
    glowna: waluta(kwota, kod),
    pomocnicza: platnosc.amountPlnMinor === null ? null : zl(platnosc.amountPlnMinor),
  }
}

export function ListaPlatnosci({
  platnosci,
  pozycje,
  kategorie,
  dzis = todayIso(),
  onPrzelacz,
  pustaTresc = 'Brak płatności w tym okresie.',
}: Props) {
  // Znacznik świeżo przełączonego wiersza — pozwala odtworzyć animację,
  // której samo przestawienie atrybutu by nie uruchomiło.
  const [ostatnio, ustawOstatnio] = useState<string | null>(null)
  const przelacz = useCallback(
    (platnosc: Platnosc) => {
      ustawOstatnio(platnosc.id)
      window.setTimeout(() => ustawOstatnio(null), 500)
      onPrzelacz(platnosc)
    },
    [onPrzelacz],
  )

  if (platnosci.length === 0) {
    return (
      <p className="pusto">
        <strong>{pustaTresc}</strong>
      </p>
    )
  }

  return (
    <ul className="platnosci wjazd-listy">
      {platnosci.map((platnosc) => {
        const pozycja = pozycje.get(platnosc.itemId)
        const kategoria =
          pozycja?.categoryId === null || pozycja?.categoryId === undefined
            ? undefined
            : kategorie.get(pozycja.categoryId)
        const stan = stanTerminu(platnosc, dzis)
        const { glowna, pomocnicza } = kwoty(platnosc)
        const zaplacona = platnosc.status === 'paid'

        return (
          <li
            key={platnosc.id}
            className={[
              'platnosc',
              zaplacona ? 'platnosc--zaplacona' : '',
              platnosc.status === 'skipped' ? 'platnosc--pominieta' : '',
              stan === 'zalegle' ? 'platnosc--zalegla' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className="platnosc__termin">
              <span className="platnosc__dzien">{platnosc.dueDate.slice(8)}</span>
              <span className="platnosc__miesiac">{skrotMiesiaca(platnosc.dueDate)}</span>
            </div>

            <div className="platnosc__opis">
              <p className="platnosc__tytul">{pozycja?.title ?? 'Pozycja usunięta'}</p>
              <p className="platnosc__meta">
                {kategoria !== undefined ? (
                  <>
                    <span
                      className="kropka-kategorii"
                      style={{ background: kategoria.color }}
                      aria-hidden="true"
                    />
                    {kategoria.name}
                  </>
                ) : (
                  'Bez kategorii'
                )}
                {stan === 'zalegle' ? <span className="platnosc__znacznik">po terminie</span> : null}
                {stan === 'dzis' ? <span className="platnosc__znacznik">dziś</span> : null}
              </p>
            </div>

            <div className="platnosc__kwoty">
              <p className="platnosc__kwota">{glowna}</p>
              {pomocnicza !== null ? <p className="platnosc__kwota-pln">{pomocnicza}</p> : null}
            </div>

            <button
              type="button"
              className={
                ostatnio === platnosc.id
                  ? 'platnosc__odhacz platnosc__odhacz--swieze'
                  : 'platnosc__odhacz'
              }
              aria-pressed={zaplacona}
              aria-label={
                zaplacona
                  ? `Cofnij zapłatę: ${pozycja?.title ?? 'płatność'}`
                  : `Oznacz jako zapłacone: ${pozycja?.title ?? 'płatność'}`
              }
              onClick={() => przelacz(platnosc)}
            >
              <IkonaPtaszek width={16} height={16} />
            </button>
          </li>
        )
      })}
    </ul>
  )
}
